import React, { Fragment, useEffect, useState } from "react";
import styled from "styled-components";
import CountUp from "react-countup";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";

import {
  deleteOrder,
  getOrders,
  updateOrderStatus,
} from "../store/slices/orders/asyncThunks";

import {
  FiBarChart2,
  FiCalendar,
  FiTrendingUp,
  FiMapPin,
  FiPhone,
  FiDollarSign,
  FiTrash2,
  FiMessageCircle,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from "react-icons/fi";
import { MdAccessTime, MdCheckCircle, MdCancel } from "react-icons/md";
import { BiMailSend } from "react-icons/bi";
import { FaFilter, FaMapMarkedAlt, FaTimes } from "react-icons/fa";
import { FaTicket } from "react-icons/fa6";

import { FRONT_URL } from "../utils/constants";
import { formatDate, priceFormatter } from "../utils/utils";

import AdminContainer from "../components/AdminContainer";
import AdminTitle from "../components/utils/AdminTitle";
import AdminToast from "../components/utils/AdminToast";
import {
  selectOrders,
  setCurrentPage,
  setSortByStatus,
} from "../store/slices/orders/slice";

const OrdersPage = () => {
  const {
    data,
    loading,
    pagination,
    lastMonthRevenue,
    thisMonthRevenue,
    totalRevenue,
    updateStatusLoading,
    deleteLoading,
    sortByStatus,
  } = useSelector(selectOrders);

  const dispatch = useDispatch();
  const [orderId, setOrderId] = useState(null);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [openPhoneModal, setOpenPhoneModal] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);
  const [openMapModal, setOpenMapModal] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [toast, setToast] = useState(null);

  const handleAddressClick = (order) => {
    const fullAddress = `${order.address}, ${order.zipCode} ${order.city}`;
    setSelectedAddress(fullAddress);
    setOpenMapModal(true);
  };

  const handlePhoneClick = (phone) => {
    setSelectedPhoneNumber(phone);
    setOpenPhoneModal(true);
  };

  const handlePageChange = (newPage) => {
    if (
      newPage !== pagination.currentPage &&
      newPage >= 1 &&
      newPage <= pagination.totalPages
    ) {
      dispatch(setCurrentPage(newPage));
      dispatch(
        getOrders({
          page: newPage,
          limit: pagination.limit,
          sortByStatus: sortByStatus,
        })
      )
        .unwrap()
        .catch((err) =>
          setToast({
            type: "error",
            message: err.message || "Greška pri učitavanju porudžbina.",
          })
        );
    }
  };

  const handleSortToggle = () => {
    const newSortValue = !sortByStatus;

    dispatch(setSortByStatus(newSortValue));
    dispatch(setCurrentPage(1));

    dispatch(
      getOrders({
        page: 1,
        limit: pagination.limit,
        sortByStatus: newSortValue,
      })
    )
      .unwrap()
      .catch((err) =>
        setToast({
          type: "error",
          message: err.message || "Greška pri sortiranju porudžbina.",
        })
      );
  };

  const handleDeleteOrder = (id) => {
    dispatch(deleteOrder(id))
      .unwrap()
      .then(() => {
        setOpenRemoveModal(false);
        setToast({ type: "success", message: "Porudžbina je obrisana." });
        if (data.length === 1 && pagination.currentPage > 1) {
          const newPage = pagination.currentPage - 1;
          dispatch(setCurrentPage(newPage));
          dispatch(
            getOrders({
              page: newPage,
              limit: pagination.limit,
              sortByStatus: sortByStatus,
            })
          );
        } else {
          dispatch(
            getOrders({
              page: pagination.currentPage,
              limit: pagination.limit,
              sortByStatus: sortByStatus,
            })
          );
        }
      })
      .catch((err) =>
        setToast({
          type: "error",
          message: err.message || "Greška pri brisanju porudžbine.",
        })
      );
  };

  useEffect(() => {
    dispatch(
      getOrders({
        page: 1,
        limit: 12,
        sortByStatus: sortByStatus,
      })
    )
      .unwrap()
      .catch((err) =>
        setToast({
          type: "error",
          message: err.message || "Greška pri učitavanju porudžbina.",
        })
      );
    // eslint-disable-next-line
  }, [dispatch]);

  const getStatusLabel = (status) => {
    switch (status) {
      case "pending":
        return {
          label: "Na čekanju",
          icon: <MdAccessTime size={18} color="#f5a623" />,
        };
      case "delivered":
        return {
          label: "Isporučeno",
          icon: <MdCheckCircle size={18} color="#27ae60" />,
        };
      case "canceled":
        return {
          label: "Otkazano",
          icon: <MdCancel size={18} color="#c0392b" />,
        };
      default:
        return {
          label: "Nepoznat status",
          icon: <MdAccessTime size={18} color="#999" />,
        };
    }
  };

  const handleUpdateStatus = async (type) => {
    dispatch(updateOrderStatus({ id: orderId, status: type }))
      .unwrap()
      .then(() => {
        setOpenStatusModal(false);
        setToast({ type: "success", message: "Status porudžbine je izmenjen." });
      })
      .catch((err) =>
        setToast({
          type: "error",
          message: err.message || "Greška pri izmeni statusa.",
        })
      );
  };

  const renderPaginationNumbers = () => {
    const { currentPage, totalPages } = pagination;
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PageNumber
          key={i}
          $active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </PageNumber>
      );
    }

    return pages;
  };

  useEffect(() => {
    if (openPhoneModal || openStatusModal || openRemoveModal || openMapModal) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "auto";
    }
  }, [openPhoneModal, openMapModal, openRemoveModal, openStatusModal]);

  return (
    <AdminContainer>
      {toast && (
        <AdminToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <AdminTitle kanji="注">Porudžbine</AdminTitle>
      {loading && pagination.currentPage === 1 ? (
        <>
          <HeaderRow>
            <StyledH2>Sve porudžbine (0):</StyledH2>
            <SortButton onClick={handleSortToggle}>
              <FaFilter size={18} />
              <span>{sortByStatus ? "Sve porudžbine" : "Samo na čekanju"}</span>
            </SortButton>
          </HeaderRow>

          <OrdersList style={{ alignItems: "center" }}>
            <l-line-spinner size="32" stroke="3" speed="1" color="#fe5f55" />
          </OrdersList>
        </>
      ) : (
        <>
          <HeaderRow>
            <StyledH2>Sve porudžbine ({pagination.totalOrders}):</StyledH2>
            <SortButton onClick={handleSortToggle}>
              <FaFilter size={18} />
              <span>{sortByStatus ? "Sve porudžbine" : "Samo na čekanju"}</span>
            </SortButton>
          </HeaderRow>

          {loading ? (
            <OrdersList style={{ alignItems: "center" }}>
              <l-line-spinner size="32" stroke="3" speed="1" color="#fe5f55" />
            </OrdersList>
          ) : (
            <>
              <OrdersList>
                {data.map((order) => (
                  <OrderCard key={order.id}>
                    <CardHeader>
                      <HeadLeft>
                        <OrderName>
                          {order.firstName} {order.lastName}
                        </OrderName>
                        <HeadMeta>{formatDate(order.createdAt)}</HeadMeta>
                      </HeadLeft>

                      <HeadRight>
                        <StatusContainer
                          $cursor={true}
                          onClick={() => {
                            setOrderId(order.id);
                            setOpenStatusModal(true);
                          }}
                        >
                          {getStatusLabel(order.status).icon}
                          <OrderValue style={{ whiteSpace: "nowrap" }}>
                            {getStatusLabel(order.status).label}
                          </OrderValue>
                        </StatusContainer>

                        <Total>
                          {order.totalPrice
                            ? `${(
                                order.totalPrice - order.deliveryPrice
                              ).toLocaleString()} RSD`
                            : "—"}
                        </Total>

                        <RemoveBtn
                          onClick={() => {
                            setOrderId(order.id);
                            setOpenRemoveModal(true);
                          }}
                        >
                          <FiTrash2 size={15} />
                        </RemoveBtn>
                      </HeadRight>
                    </CardHeader>

                    <CardBody>
                      <MetaCol>
                        <OrderRow>
                          <FiMapPin size={15} />
                          <OrderValue
                            $hover
                            onClick={() => handleAddressClick(order)}
                          >
                            {order.address}, {order.city}, {order.zipCode}
                          </OrderValue>
                        </OrderRow>

                        <OrderRow>
                          <FiPhone size={15} />
                          <OrderValue
                            $hover
                            onClick={() => handlePhoneClick(order.phone)}
                          >
                            {order.phone}
                          </OrderValue>
                        </OrderRow>

                        <OrderRow>
                          <BiMailSend size={16} />
                          <OrderValue
                            as="a"
                            href={`mailto:${order.email}`}
                            $hover
                            style={{ textDecoration: "none" }}
                          >
                            {order.email}
                          </OrderValue>
                        </OrderRow>

                        {order.coupon && (
                          <OrderRow>
                            <FaTicket size={15} />
                            <OrderValue>
                              {order.coupon.code} -{order.coupon.discount}%
                            </OrderValue>
                          </OrderRow>
                        )}
                      </MetaCol>

                      <OrderItems>
                        {order.orderItems.map((item, index) => (
                          <OrderCardRow key={index}>
                            <Image
                              src={`${FRONT_URL}/thumbnails/${item.thumbnailImage}`}
                              alt={item.name}
                            />
                            <Info>
                              <Title>{item.name}</Title>
                              <Details>
                                <span>{item.size}</span>
                                <span>×{item.quantity}</span>
                                {item?.colorString && (
                                  <span>{item.colorString}</span>
                                )}
                              </Details>
                            </Info>
                            <Price>
                              {priceFormatter(item.price * item.quantity)} RSD
                            </Price>
                          </OrderCardRow>
                        ))}
                      </OrderItems>
                    </CardBody>
                  </OrderCard>
                ))}
              </OrdersList>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <PaginationContainer>
                  <PaginationButton
                    onClick={() => handlePageChange(1)}
                    disabled={pagination.currentPage === 1}
                  >
                    <FiChevronsLeft size={18} />
                  </PaginationButton>

                  <PaginationButton
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPreviousPage}
                  >
                    <FiChevronLeft size={18} />
                  </PaginationButton>

                  <PaginationNumbers>
                    {renderPaginationNumbers()}
                  </PaginationNumbers>

                  <PaginationButton
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                  >
                    <FiChevronRight size={18} />
                  </PaginationButton>

                  <PaginationButton
                    onClick={() => handlePageChange(pagination.totalPages)}
                    disabled={pagination.currentPage === pagination.totalPages}
                  >
                    <FiChevronsRight size={18} />
                  </PaginationButton>

                  <PaginationInfo>
                    Strana {pagination.currentPage} od {pagination.totalPages}
                  </PaginationInfo>
                </PaginationContainer>
              )}
            </>
          )}
        </>
      )}

      <AnimatePresence>
        {openRemoveModal && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpenRemoveModal(false)}
          >
            <ConfirmBox
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn onClick={() => setOpenRemoveModal(false)}>
                <FaTimes />
              </CloseBtn>
              <h3>Potvrdi brisanje</h3>
              <p>Da li si siguran da želiš da obrišeš ovu porudžbinu?</p>
              <ModalActions>
                <Cancel
                  onClick={() => {
                    if (!deleteLoading) {
                      setOpenRemoveModal(false);
                    }
                  }}
                >
                  Odustani
                </Cancel>
                {!deleteLoading ? (
                  <Confirm onClick={() => handleDeleteOrder(orderId)}>
                    Obriši
                  </Confirm>
                ) : (
                  <Confirm $disabled>
                    Obriši
                    <l-line-spinner
                      size="16"
                      stroke="2"
                      speed="1"
                      color="#fff"
                    />
                  </Confirm>
                )}
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {openPhoneModal && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpenPhoneModal(false)}
          >
            <ConfirmBox
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn onClick={() => setOpenPhoneModal(false)}>
                <FaTimes />
              </CloseBtn>
              <h3 style={{ marginBottom: 0 }}>Kontaktiraj kupca</h3>

              <CardValue
                style={{ color: "white", fontFamily: "Poppins-Medium" }}
              >
                {selectedPhoneNumber}
              </CardValue>

              <ModalActions style={{ marginTop: 10 }}>
                <StatusButton
                  $type="deliver"
                  onClick={() => {
                    window.location.href = `tel:${selectedPhoneNumber}`;
                    setOpenPhoneModal(false);
                  }}
                >
                  <FiPhone size={16} />
                  Pozovi
                </StatusButton>
                <StatusButton
                  $type="cancel"
                  onClick={() => {
                    window.location.href = `sms:${selectedPhoneNumber}`;
                    setOpenPhoneModal(false);
                  }}
                >
                  <FiMessageCircle size={16} />
                  SMS
                </StatusButton>
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {openMapModal && selectedAddress && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpenMapModal(false)}
          >
            <ConfirmBox
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "90vw", maxHeight: "80vh" }}
            >
              <CloseBtn onClick={() => setOpenMapModal(false)}>
                <FaTimes />
              </CloseBtn>
              <h3 style={{ marginBottom: 0 }}>Lokacija kupca</h3>
              <CardValue
                style={{ color: "white", fontFamily: "Poppins-Medium" }}
              >
                {selectedAddress}
              </CardValue>

              <div
                style={{ width: "100%", height: "50vh", margin: "10px 0px" }}
              >
                <iframe
                  title="Google Map"
                  width="100%"
                  height="100%"
                  style={{ border: 0, borderRadius: "8px" }}
                  loading="lazy"
                  allowFullScreen
                  src={`https://www.google.com/maps?q=${encodeURIComponent(
                    selectedAddress
                  )}&output=embed`}
                ></iframe>
              </div>

              <ModalActions style={{ marginTop: 15 }}>
                <StatusButton
                  $type="deliver"
                  as="a"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    selectedAddress
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaMapMarkedAlt size={16} />
                  Otvori u Google Maps
                </StatusButton>
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {openStatusModal && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpenStatusModal(false)}
          >
            <ConfirmBox
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn onClick={() => setOpenStatusModal(false)}>
                <FaTimes />
              </CloseBtn>
              <h3>Promeni status porudžbine</h3>
              <p>Izaberi novi status za ovu porudžbinu:</p>
              <ModalActions>
                {updateStatusLoading && (
                  <StatusButtonLoading>
                    <l-line-spinner
                      size="16"
                      stroke="2"
                      speed="1"
                      color="#fff"
                    />
                  </StatusButtonLoading>
                )}

                {!updateStatusLoading && (
                  <>
                    <StatusButton
                      $type="pending"
                      onClick={() => handleUpdateStatus("pending")}
                    >
                      Na čekanju
                    </StatusButton>
                    <StatusButton
                      $type="deliver"
                      onClick={() => handleUpdateStatus("delivered")}
                    >
                      Isporučeno
                    </StatusButton>
                    <StatusButton
                      $type="cancel"
                      onClick={() => handleUpdateStatus("canceled")}
                    >
                      Otkaži
                    </StatusButton>
                  </>
                )}
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </AdminContainer>
  );
};

export default OrdersPage;

// Existing styled components
const RemoveBtn = styled.button`
  background: transparent;
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  color: var(--muted, #8e8c94);
  z-index: 1;
  cursor: pointer;
  width: 3.2rem;
  height: 3.2rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.4rem;
  outline: none;
  display: flex;
  transition: all 0.25s;

  &:hover {
    color: var(--red, #fe5f55);
    border-color: var(--red, #fe5f55);
  }
`;

const SendMailBtn = styled.a`
  background-color: transparent;
  cursor: pointer;
  border: none;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: #fff;
  }

  &:hover {
    svg {
      color: #fe5f55;
    }
  }
`;

const StatsGrid = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
  margin-bottom: 3rem;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2rem;
  justify-content: space-between;
  width: 100%;
  flex-wrap: wrap;
`;

const StyledH2 = styled.h2`
  font-size: 1.8rem;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text, #f4f2ee);
  margin-top: 2rem;
`;

const CardTitle = styled.h4`
  font-size: 1.4rem;
  font-family: "Poppins-SemiBold";
  color: var(--muted, #8e8c94);
`;

const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  background: transparent;
  color: #fe5f55;
  border: 1px solid #fe5f55;
  border-radius: 0.4rem;
  padding: 0.6rem 1.2rem;
  font-family: "Poppins-Medium";
  cursor: pointer;
  font-size: 1.2rem;

  &:hover {
    background: #fe5f55;
    color: black;

    svg {
      color: black;
    }
  }

  svg {
    transition: 0.2s ease-in-out;
  }
`;

const CardValue = styled.span`
  font-size: 1.6rem;
  font-family: "Poppins-Bold";
  color: #fe5f55;
`;

const StatCard = styled.div`
  background: var(--bg1, #101013);
  padding: 2rem;
  border-radius: 0.4rem;
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  color: white;
  display: flex;
  gap: 1rem;
  align-items: center;
  min-width: 30rem;
  flex: 1;
  @media (max-width: 350px) {
    width: 100%;
    flex: unset;
    min-width: unset;
  }
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
`;

const OrderCard = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--bg1, #101013);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.4rem;
  position: relative;
  height: fit-content;
  transition: border-color 0.25s;

  &:hover {
    border-color: var(--line2, rgba(255, 255, 255, 0.14));
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.6rem;
  flex-wrap: wrap;
  padding: 1.4rem 1.8rem;
  border-bottom: 1px solid var(--line, rgba(255, 255, 255, 0.07));
`;

const HeadLeft = styled.div`
  display: flex;
  align-items: baseline;
  gap: 1.2rem;
  flex-wrap: wrap;
`;

const HeadMeta = styled.span`
  font-family: "Poppins-Regular";
  font-size: 1.2rem;
  color: var(--muted2, #5c5a63);
`;

const HeadRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1.4rem;
  flex-wrap: wrap;
`;

const Total = styled.span`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  font-size: 1.8rem;
  letter-spacing: 0.04em;
  color: var(--red, #fe5f55);
`;

const CardBody = styled.div`
  display: grid;
  grid-template-columns: minmax(24rem, 1fr) 1.4fr;
  gap: 2.4rem;
  padding: 1.6rem 1.8rem;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const MetaCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const OrderItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const OrderCardRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1.2rem;
  background: var(--bg0, #0a0a0c);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.4rem;
  padding: 0.8rem 1.2rem;
`;

const OrderCardColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const LestColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1rem;
  justify-content: space-between;
  justify-self: flex-end;
  flex: 1;
  @media (max-width: 768px) {
    flex-direction: row;
  }
`;

const OrderName = styled.span`
  color: var(--text, #f4f2ee);
  font-size: 1.5rem;
  font-family: "Poppins-SemiBold";
`;

const OrderValue = styled.span`
  font-family: "Poppins-Regular";
  font-size: 1.3rem;
  color: var(--muted, #8e8c94);
  cursor: ${(props) => (props.$hover ? "pointer" : "default")};
  &:hover {
    color: ${(props) => (props.$hover ? "#fe5f55" : "#fff")};
  }
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.7rem;
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  border-radius: 10rem;
  padding: 0.5rem 1.3rem;
  cursor: ${(props) => (props.$cursor ? "pointer" : "default")};
  transition: border-color 0.25s;

  &:hover {
    border-color: ${(props) =>
      props.$cursor ? "var(--red, #fe5f55)" : "var(--line2)"};

    span {
      color: ${(props) => (props.$cursor ? "var(--red, #fe5f55)" : "")};
    }
  }
`;

const OrderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.9rem;

  & > svg {
    color: var(--red, #fe5f55);
    flex-shrink: 0;
  }
`;

const Image = styled.img`
  width: 4.4rem;
  height: 5.4rem;
  object-fit: cover;
  border-radius: 0.3rem;
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  flex-shrink: 0;
`;

const Info = styled.div`
  flex: 1;
  color: white;
`;

const Title = styled.div`
  font-size: 1.2rem;
  font-family: "Poppins-SemiBold";
  margin-bottom: 0.3rem;
`;

const Details = styled.div`
  font-size: 1.15rem;
  color: var(--muted, #8e8c94);
  display: flex;
  font-family: "Poppins-Medium";
  flex-direction: row;
  gap: 0.4rem;

  span + span::before {
    content: "· ";
    color: var(--muted2, #5c5a63);
  }
`;

const Price = styled.div`
  font-size: 1.35rem;
  color: var(--red, #fe5f55);
  font-family: "Anton", sans-serif;
  font-weight: 400;
  letter-spacing: 0.04em;
  white-space: nowrap;
`;

// New pagination styled components
const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 3rem;
  flex-wrap: wrap;
`;

const PaginationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  background: var(--bg1, #101013);
  color: ${(props) => (props.disabled ? "#666" : "#fff")};
  border-radius: 0.4rem;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease-in-out;

  &:hover:not(:disabled) {
    background: #fe5f55;
    border-color: #fe5f55;
    color: #000;
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const PaginationNumbers = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PageNumber = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  border: 1px solid ${(props) => (props.$active ? "#fe5f55" : "var(--line, rgba(255, 255, 255, 0.07))")};
  background: ${(props) => (props.$active ? "#fe5f55" : "var(--bg1, #101013)")};
  color: ${(props) => (props.$active ? "#000" : "#fff")};
  border-radius: 0.4rem;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  font-family: "Poppins-Medium";
  font-size: 1.4rem;

  &:hover {
    background: #fe5f55;
    border-color: #fe5f55;
    color: #000;
  }
`;

const PaginationInfo = styled.span`
  color: var(--muted, #8e8c94);
  font-family: "Poppins-Medium";
  font-size: 1.4rem;
  margin-left: 1rem;

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 1rem;
  }
`;

// Existing modal styled components
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: #fff;
  border-radius: 50%;
  font-size: 1.6rem;
  width: 3rem;
  height: 3rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-backdrop-filter: blur(4px);
  background-color: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(4px);
  transition: background 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: var(--red, #fe5f55);
  }
`;

const ConfirmBox = styled.div`
  background: #1a1a1a;
  padding: 3rem 2rem;
  border-radius: 1.6rem;
  max-width: 400px;
  width: 100%;
  color: white;
  position: relative;
  text-align: center;

  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
    color: #fe5f55;
    font-family: "Poppins-SemiBold";
  }

  p {
    font-size: 1.3rem;
    margin-bottom: 2rem;
    font-family: "Poppins-Medium";
    color: var(--muted, #8e8c94);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const Cancel = styled.button`
  padding: 0.8rem 2rem;
  border-radius: 0.4rem;
  background: var(--line, rgba(255, 255, 255, 0.07));
  color: white;
  border: none;
  font-family: "Poppins-SemiBold";
  cursor: pointer;

  &:hover {
    background: var(--line2, rgba(255, 255, 255, 0.14));
  }
`;

const Confirm = styled.button`
  padding: 0.8rem 2rem;
  border-radius: 0.4rem;
  background: #fe5f55;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.65rem;
  font-size: 1.4rem;
  font-family: "Poppins-SemiBold";
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};

  &:hover {
    background: var(--red, #fe5f55);
  }
`;

const StatusButtonLoading = styled.button`
  padding: 0.6rem 1.2rem;
  border-radius: 0.4rem;
  font-size: 1.2rem;
  font-family: "Poppins-SemiBold";
  border: none;
  cursor: not-allowed;
  transition: 0.2s ease-in-out;
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: center;
  gap: 0.5rem;
  min-width: 10rem;
  background-color: transparent;
  border: 1.5px solid #fe5f55;
`;

const StatusButton = styled.button`
  padding: 0.6rem 1.2rem;
  border-radius: 0.4rem;
  font-size: 1.2rem;
  font-family: "Poppins-SemiBold";
  border: 1.5px solid transparent;
  cursor: pointer;
  transition: 0.2s ease-in-out;
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 0.5rem;
  text-decoration: none;

  ${(props) =>
    props.$type === "deliver"
      ? `
    background: #27ae60;
    color: white;

    &:hover {
      background: #2ecc71;
    }
  `
      : props.$type === "pending"
      ? `
    background: #d39e00;
    color: white;

    &:hover {
      background: #e6b400;
    }
  `
      : `
    background: #c0392b;
    color: white;

    &:hover {
      background: #e74c3c;
    }
  `};
`;
